import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon, ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { HowStep } from '../../types/howItWorks';
import { ScreenId } from '../../types/navigation';
import { NewBadge } from '../sf/NewBadge';
import { CadencePill } from './CadencePill';
import { cadenceStyles } from './cadenceStyles';
import { RuleKeyChip } from './RuleKeyChip';
interface StepCardProps {
  step: HowStep;
  expanded: boolean;
  isLast: boolean;
  onToggle: () => void;
  onNavigate: (screen: ScreenId) => void;
  children?: React.ReactNode;
}
function FlowArrow() {
  return <div className="hidden items-center justify-center text-sf-weak lg:flex" aria-hidden="true" data-id="element-413" data-mp="k1x8zpyoabis">
      <ArrowRightIcon className="h-4 w-4" data-id="element-414" data-mp="f3vnyzhos24o" />
    </div>;
}
export function StepCard({
  step,
  expanded,
  isLast,
  onToggle,
  onNavigate,
  children
}: StepCardProps) {
  const style = cadenceStyles[step.cadence];
  const panelId = `step-explain-${step.id}`;
  return <li id={`step-${step.id}`} className="scroll-mt-28" data-id="element-415" data-mp="1kq1rf5mmzpj7">
      <article className="overflow-hidden rounded border border-sf-border bg-white shadow-card" style={{
      borderLeft: `4px solid ${style.hex}`
    }} data-id="element-416" data-mp="1ksnyl45m830w">
        <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls={panelId} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ease-out hover:bg-[#FAFAF9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sf-blue" data-id="element-417" data-mp="1b1a1t451thbg">
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white ${style.solid}`} data-id="element-418" data-mp="s25ymc06uhrp">{step.number}</span>
          <h2 className="text-[15px] font-bold text-sf-text" data-id="element-419" data-mp="x2tnos4uwoja">{step.title}</h2>
          <CadencePill cadence={step.cadence} data-id="element-420" data-mp="2mg6wounwz0qq" />
          <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-sf-blue" data-id="element-421" data-mp="2regs96djw3cv">
            {expanded ? 'Hide explanation' : 'Explain this step'}
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-422" data-mp="2oxqth3tjudpl" />
          </span>
        </button>

        <div className="grid grid-cols-1 gap-3 border-t border-sf-border px-4 py-3 lg:grid-cols-[minmax(0,1fr)_20px_minmax(0,1.25fr)_20px_minmax(0,0.9fr)]" data-id="element-423" data-mp="2fznhay4getag">
          <section aria-label="Input" data-id="element-424" data-mp="38ocamaz9pubn">
            <h3 className="mb-1.5 text-[11px] font-semibold text-sf-weak" data-id="element-425" data-mp="38n9jq31y3rvq">Input</h3>
            <dl className="space-y-1" data-id="element-426" data-mp="33qavg14yqf5g">
              {step.inputs.map(input => <div key={input.label} className="flex justify-between gap-3 text-[13px]" data-id="element-427" data-mp="2yx7yrk4w2pl1">
                  <dt className="text-sf-weak" data-id="element-428" data-mp="2tzy7dyxxw3uk">{input.label}</dt>
                  <dd className="text-right font-medium tabular-nums" data-id="element-429" data-mp="3rekamk7o84nm">{input.value}</dd>
                </div>)}
            </dl>
          </section>
          <FlowArrow data-id="element-430" data-mp="3mm9c9lskwqx6" />
          <section aria-label="Rule applied" className="lg:border-l lg:border-r lg:border-sf-border lg:px-4" data-id="element-431" data-mp="3mm0w9l15t5hr">
            <h3 className="mb-1.5 text-[11px] font-semibold text-sf-weak" data-id="element-432" data-mp="2spaj3aqm1s1">Rule applied</h3>
            <p className="text-[13px] leading-snug" data-id="element-433" data-mp="2uigshhfujzi">{step.rule.summary}</p>
            {step.newNote && <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-434" data-mp="cqv9bc18yrso">
                <NewBadge data-id="element-435" data-mp="coilii8t5n7p" />
                {step.newNote}
              </p>}
            {step.rule.ruleKeys.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-436" data-mp="lqrs4x0ygwft">
                {step.rule.ruleKeys.map(key => <RuleKeyChip key={key} ruleKey={key} onClick={() => onNavigate('plans')} data-id="element-437" data-mp="qjky1o85uath" />)}
              </div>}
          </section>
          <FlowArrow data-id="element-438" data-mp="10gpnbwxaymei" />
          <section aria-label="Output" className={`rounded border px-3 py-2 ${style.bg} ${style.border}`} data-id="element-439" data-mp="10h8vilx5hwfc">
            <h3 className={`text-[11px] font-semibold ${style.text}`} data-id="element-440" data-mp="14j2o06x6zu4f">Output · {step.output.label}</h3>
            <p className="mt-0.5 text-lg font-bold leading-tight tabular-nums text-sf-text" data-id="element-441" data-mp="225mi8vcvlo38">{step.output.value}</p>
            {step.output.detail && <p className="mt-0.5 text-xs text-sf-text" data-id="element-442" data-mp="1x605en0ym9uy">{step.output.detail}</p>}
          </section>
        </div>

        {children && <div className="border-t border-sf-border px-4 py-3" data-id="element-443" data-mp="26xw1jsjhjpwj">{children}</div>}

        <AnimatePresence initial={false} data-id="element-444" data-mp="27009clki9923">
          {expanded && <motion.div id={panelId} initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-445" data-mp="1j8l8kx8dmzsy">
              <div className="space-y-2 border-t border-sf-border bg-[#FAFAF9] px-4 py-3 text-[13px] leading-relaxed" data-id="element-446" data-mp="33nwpjpc162cs">
                {step.explanation.map(paragraph => <p key={paragraph} data-id="element-447" data-mp="2yok0qz1lml7k">{paragraph}</p>)}
                {step.link && <button type="button" onClick={() => onNavigate(step.link!.screen)} className="inline-flex items-center gap-1 rounded text-[13px] font-semibold text-sf-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-448" data-mp="377x8amksmjai">
                    {step.link.label} <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-449" data-mp="3pxmzkzpckmxo" />
                  </button>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </article>
      {!isLast && <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-sf-weak" aria-hidden="true" data-id="element-450" data-mp="3g4oalgpxuudj">
          <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-451" data-mp="cq23f5deeq59" />
          Output feeds step {step.number + 1}
        </div>}
    </li>;
}